/**
 * @file onboardingResume.js
 * @description Pure helper that lets agency onboarding RESUME instead of
 *   restarting at step 1.
 *
 *   Agency onboarding is two steps: (1) NPI verification, which the
 *   stamp-npi-verification edge function persists to agency_profiles the moment
 *   it succeeds, and (2) the profile form (display name, legal name, ZIP,
 *   about, ...), which is only saved when the agency finishes. App.js hard-gates
 *   every non-onboarded user into the modal on login, so an agency that
 *   verified its NPI and then closed the tab used to come back to an empty
 *   step 1 and be asked for the NPI again. Given what is already saved, this
 *   returns the step to open on and the form fields to prefill.
 *
 *   Nothing here relaxes a gate: an agency that has not verified an NPI still
 *   starts at step 1, and canProceed()/handleComplete() in Onboarding.jsx and
 *   the server-side NPI/BAA triggers are unchanged. Prefilled values are only
 *   ever the agency's own saved ones, and the NPI field stays editable from
 *   step 1 (via Back).
 *
 *   Therapist onboarding is intentionally NOT covered: its saved shape
 *   (therapist_profiles) is much larger and maps differently, so it still
 *   starts at step 1.
 * @module lib/onboardingResume
 */
import { toDisplayName } from "./agencyName";

const str = (v) => (typeof v === "string" ? v : "");

/**
 * @param {Object|null|undefined} saved - the UI-shaped agency profile from
 *   profileService.loadAgencyProfile(userId).profile (null/undefined when no
 *   agency_profiles row exists yet).
 * @returns {{ startStep: 1|2, data: Object }} step to open on, and the Onboarding
 *   `data` fields to prefill (empty object when there is nothing to resume).
 */
export function agencyResume(saved) {
  if (!saved || typeof saved !== "object") return { startStep: 1, data: {} };

  const npiVerified = saved.npiVerified === true && /^\d{10}$/.test(str(saved.npi));
  const data = {};

  if (npiVerified) {
    data.npi = saved.npi;
    data.npiVerified = true;
    data.npiOrgName = str(saved.npiOrgName);
  }

  // Step-2 fields: only carry over what is really saved.
  if (str(saved.name).trim()) data.agencyName = saved.name;
  // dbToAgency falls back to the legal name for a missing display name, which
  // for a registry-stamped agency is ALL CAPS. Clean it up the same way the
  // NPI step's own pre-fill does; a mixed-case value is left as the agency typed it.
  const dba = toDisplayName(str(saved.dbaName));
  if (dba) data.dbaName = dba;
  for (const k of ["city", "state", "zip", "phone", "about", "email"]) {
    if (str(saved[k]).trim()) data[k] = saved[k];
  }
  if (Array.isArray(saved.languages) && saved.languages.length) data.languages = saved.languages;
  if (Array.isArray(saved.settings) && saved.settings.length) data.settings = saved.settings;

  return { startStep: npiVerified ? 2 : 1, data };
}
